import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../hooks/useApi';
import { login, register } from '../../lib/api/auth';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import SafeIcon from '../../common/SafeIcon';

export default function AuthPage({ inviteToken }) {
  const [isLogin, setIsLogin] = useState(!inviteToken);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const { execute: execLogin, loading: loginLoading } = useApi(login);
  const { execute: execRegister, loading: regLoading } = useApi(register);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // VALIDATION: Password Match
    if (!isLogin && formData.password !== formData.confirmPassword) {
      alert("Passwords do not match. Please verify your password confirmation.");
      return;
    }

    try {
      let user;
      if (isLogin) {
        user = await execLogin(formData.email, formData.password, inviteToken);
      } else {
        user = await execRegister(formData.email, formData.password, inviteToken);
      }
      setUser(user);
      navigate('/');
    } catch (err) {
      console.error("Auth Error:", err);
    }
  };

  const passwordsMatch = isLogin || !formData.confirmPassword || formData.password === formData.confirmPassword;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
        <div className="mx-auto bg-indigo-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-4 shadow-sm relative">
          <SafeIcon icon="DollarSign" className="w-8 h-8 text-indigo-600" />
          <div className="absolute -top-2 -right-4 bg-indigo-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
            v1.2
          </div>
        </div>
        <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">
          {inviteToken ? 'Join Household' : (isLogin ? 'Sign in to KidCash' : 'Create an account')}
        </h2>
        <p className="mt-2 text-sm text-gray-500">
          {isLogin ? 'Manage pocket money with ease' : 'Start tracking balances today'}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-xl sm:rounded-2xl sm:px-10 border border-gray-100">
          <form className="space-y-5" onSubmit={handleSubmit}>
            <Input 
              label="Email address" 
              type="email" 
              required 
              value={formData.email}
              onChange={e => setFormData({...formData, email: e.target.value})}
              placeholder="you@example.com"
            />
            
            <div className="relative">
              <Input 
                label="Password" 
                type={showPassword ? 'text' : 'password'} 
                required 
                minLength={6}
                value={formData.password}
                onChange={e => setFormData({...formData, password: e.target.value})}
                placeholder="••••••••"
              />
              <button 
                type="button"
                tabIndex="-1"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-[34px] p-2 text-gray-400 hover:text-indigo-600 transition-colors"
                title={showPassword ? "Hide Password" : "Show Password"}
              >
                <SafeIcon icon={showPassword ? "EyeOff" : "Eye"} className="w-4 h-4" />
              </button>
            </div>

            {!isLogin && (
              <div className="animate-in slide-in-from-top-2 duration-300">
                <Input 
                  label="Confirm Password" 
                  type={showPassword ? 'text' : 'password'} 
                  required 
                  value={formData.confirmPassword}
                  onChange={e => setFormData({...formData, confirmPassword: e.target.value})}
                  placeholder="••••••••"
                  error={!passwordsMatch ? "Passwords do not match" : ""}
                />
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full py-3 text-base shadow-indigo-200 shadow-lg" 
              loading={loginLoading || regLoading}
              disabled={!passwordsMatch}
            >
              {isLogin ? 'Sign In' : (inviteToken ? 'Join Now' : 'Create Account')}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-gray-100 text-center">
            <button 
              onClick={() => {
                setIsLogin(!isLogin);
                setFormData({ email: '', password: '', confirmPassword: '' });
              }}
              className="text-sm text-indigo-600 font-semibold hover:text-indigo-500 transition-colors"
            >
              {isLogin ? "New here? Create an account" : "Already have an account? Sign in"}
            </button>
          </div>
        </div>
        
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-400 font-medium">v1.2.0 • Supabase Live</p>
        </div>
      </div>
    </div>
  );
}